import type mapboxgl from "mapbox-gl";
import { PANEL_GUTTER, useWellCardPanelStore } from "stores/wellCardPanel";

export interface Span {
  readonly left: number;
  readonly right: number;
}

// The fit leaves at least this much map beside the card, or it ignores the card.
const MIN_UNCOVERED_PX = 120;

/**
 * Where the open card sits across the window, from the store rather than the
 * DOM: a card opened in the same task has not been laid out yet. Undefined
 * for a docked card, which is a bar, and a maximized one, which covers
 * everything.
 */
export function wellCardSpan(): Maybe<Span> {
  const panel = useWellCardPanelStore.getState();
  if (!panel.open || panel.minimized || panel.maximized) return undefined;
  const left = panel.position
    ? panel.position.x
    : window.innerWidth - PANEL_GUTTER - panel.panelWidth;
  return { left, right: left + panel.panelWidth };
}

/**
 * Adds the part of the map the card covers to the padding on the card's side,
 * so a fit centres on the map the user can still see.
 */
export function padClearOfWellCard(
  padding: number | mapboxgl.PaddingOptions,
  container: Maybe<Span>
): number | mapboxgl.PaddingOptions {
  const card = wellCardSpan();
  if (!card || !container) return padding;
  const coveredFrom = Math.max(card.left, container.left);
  const coveredTo = Math.min(card.right, container.right);
  if (coveredTo <= coveredFrom) return padding;
  const sides =
    typeof padding === "number"
      ? { top: padding, bottom: padding, left: padding, right: padding }
      : padding;
  const onRight = card.left + card.right >= container.left + container.right;
  // Up to the container's edge, so a card short of it still clears the fit.
  const added = onRight ? container.right - coveredFrom : coveredTo - container.left;
  // Padding past the container gives cameraForBounds a negative scale and a
  // NaN zoom, so too little map beside the card ignores the card.
  const spare = container.right - container.left - added - MIN_UNCOVERED_PX;
  if (spare < 0) return padding;
  // A narrow map gives up the base padding, the side away from the card first,
  // rather than centring the well under the card.
  const near = Math.min(onRight ? sides.right : sides.left, spare);
  const far = Math.min(onRight ? sides.left : sides.right, spare - near);
  return onRight
    ? { ...sides, left: far, right: near + added }
    : { ...sides, left: near + added, right: far };
}
